import React, { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { normalizeShortCode, isPlausibleShortCode } from "../utils/shortCode";

const PAGE_BG = "#F9F5F0";
const TEXT = "#1a1a1a";

/**
 * The staging host this route bounces to, e.g. https://myhoom-dev.54.235.181.63.sslip.io.
 *
 * Build config on purpose (`.env.dev` / `.env.prod`), with no fallback. A default here would put
 * the staging server's IP back into code — the exact thing this route exists to avoid — and would
 * hide a misconfigured build behind a redirect that looks like it worked.
 */
const STAGING_SITE_URL = process.env.REACT_APP_STAGING_SITE_URL;

/**
 * `/staging/qrcode` — keeps a production address in the text message while landing the
 * recipient on staging.
 *
 * Staging gift texts send `https://www.myhoom.com/staging/qrcode?shortCode=…`. That host talks to
 * the production API, so a staging code resolves to nothing. Sending the texts to the staging host
 * instead was tried on 2026-08-07 and reverted the same day: a URL containing a raw IP address
 * reads as phishing to carriers, and Twilio returned `30007 undelivered` on a controlled test where
 * only the hostname differed.
 *
 * This does not compete with the app. When the app is installed the phone matches the address in
 * the message and opens it directly, and this page is never loaded; the hop only runs when there is
 * no app, which is when it is wanted.
 *
 * Same shape as `components/appRedirect.js` — `window.location.replace` inside an effect — so Back
 * returns to the message rather than bouncing through here again.
 *
 * Spec: docs/spec/deep-links.md (AC-DEEPLINK-F1, F2, E1-E4, S1)
 */
function StagingQrRedirect() {
  const [searchParams] = useSearchParams();
  const shortCode = useMemo(
    () => normalizeShortCode(searchParams.get("shortCode")),
    [searchParams],
  );

  const isPlausible = shortCode !== "" && isPlausibleShortCode(shortCode);

  // Empty unless there is both somewhere to go and something worth sending. Building the URL and
  // deciding whether to navigate are the same decision, so they stay in one place.
  const target = useMemo(() => {
    if (!STAGING_SITE_URL || !isPlausible) return "";
    const base = STAGING_SITE_URL.replace(/\/+$/, "");
    const params = new URLSearchParams({ shortCode });
    return `${base}/qrcode?${params.toString()}`;
  }, [shortCode, isPlausible]);

  // One navigation per destination. React.StrictMode (src/index.js:11) invokes effects twice in
  // development, and without this the app fires `replace` twice for the same URL.
  const redirectedTo = useRef(null);

  useEffect(() => {
    if (!target || redirectedTo.current === target) return;
    redirectedTo.current = target;
    // replace(), not assign(): a pushed entry would make Back land here, redirect forward again,
    // and trap the visitor between the message and staging.
    window.location.replace(target);
  }, [target]);

  let message;
  if (shortCode === "") {
    message = (
      <>
        Add a short code to open your claim. Example:{" "}
        <span style={{ wordBreak: "break-all" }}>
          /staging/qrcode?shortCode=YON10KCV
        </span>
      </>
    );
  } else if (!isPlausible) {
    message = (
      <>
        That short code doesn&apos;t look right. Open the link in your text
        message again, or ask the sender to resend it.
      </>
    );
  } else if (!STAGING_SITE_URL) {
    // A build with nowhere to send people. Saying so beats navigating to "undefined/qrcode?…".
    message = <>This build has no staging site configured, so there&apos;s nowhere to send you.</>;
  } else {
    message = <>Taking you to the staging site&hellip;</>;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        fontFamily: '"Poppins", sans-serif',
        color: TEXT,
        padding: "48px 20px 40px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <h1
        style={{
          margin: "0 0 16px",
          fontSize: "1.5rem",
          fontWeight: 700,
          textAlign: "center",
          letterSpacing: "-0.02em",
        }}
      >
        Claim your beer!
      </h1>
      <p
        style={{
          marginTop: 8,
          fontSize: "0.95rem",
          fontWeight: 600,
          textAlign: "center",
          color: "#444",
          maxWidth: 320,
          lineHeight: 1.5,
        }}
      >
        {message}
      </p>
    </div>
  );
}

export default StagingQrRedirect;
